import { useEffect, useState } from 'react'
import { Blasenfarben } from './Blasenfarben'
import { MeineBlasenfarben } from './MeineBlasenfarben'
import { Chatgrund } from './Chatgrund'
import { Symbolbild } from './Symbolbild'
import { Zuruecksetzen } from './Zuruecksetzen'
import { Zuordnungsblatt } from './Zuordnungsblatt'
import { Willkommenskarte } from './Willkommenskarte'
import { mitZeitraum } from '../export/zeitraum'
import { Kategorien, type Kategorie } from '../settings/Kategorien'
import { Netzstreifen } from '../../components/Netzstreifen'
import { rueckfrage } from '../../components/Rueckfrage'
import { ApiError } from '../../api/client'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  useCreateInvitation,
  useRevokeInvitation,
  useDecideRequest,
  useSendWelcome,
  useInvitations,
  useJoinRequests,
  useLeaveTrip,
  useMembers,
  useRemoveMember,
  useSetAktiv,
  useSetApproval,
  useSetCutoff,
  useSetMuted,
  useMe,
  useSetTripState,
  useTrip,
  useUpdateTrip,
} from '../../hooks/queries'
import type { JoinRequest, Trip, TripState } from '../../api/types'
import { dayMonth, initials, tripPeriod } from '../../lib/format'
import { versatzAuswahl, versatzText } from '../../lib/ortszeit'
import {
  BackIcon,
  BuchIcon,
  ExitIcon,
  GlockeAusIcon,
  GlockeIcon,
  GruppeIcon,
  InfoIcon,
  LinkIcon,
  MondIcon,
  PauseIcon,
  PersonPlusIcon,
  PhotoIcon,
  PlayIcon,
  ResetIcon,
  SendIcon,
  XIcon,
} from '../../components/Icons'
import { Erreichbarkeit } from '../../components/Erreichbarkeit'
import { Restzeichen } from '../../components/Restzeichen'
import { useTextgrenzen } from '../../hooks/useTextgrenzen'
import { useTexte, WORTLAUT } from '../../texte'

/**
 * Die drei Zustände in Worten.
 *
 * Die Rückfrage steht dort, wo etwas verschwindet -- beim Öffnen und beim
 * Abschließen braucht es keine, beim Zurückziehen schon.
 */
const STUFEN: Array<{ wert: TripState; titel: string; text: string; frage?: string }> = [
  { wert: 'active', ...WORTLAUT.reise.info.zustand.active },
  // **Der Zustand heißt nach dem, was er sperrt** (#26). „Abgeschlossen" hat
  // ein Ende behauptet und deshalb nur die Hälfte seiner Verwendung getroffen:
  // aus dem Betrieb kommt dieselbe Sperre auch *vor* der Reise, damit alle
  // schon eingeladen sind und noch niemand schreibt.
  { wert: 'closed', ...WORTLAUT.reise.info.zustand.closed },
  { wert: 'private', ...WORTLAUT.reise.info.zustand.private },
]

/**
 * Titel, Beschreibung und Reisezeitraum ändern.
 *
 * Aufgeklappt nur auf Wunsch: die Reiseseite ist ohnehin lang, und geändert
 * wird selten. Geschickt wird nur, was sich wirklich geändert hat — der
 * Server unterscheidet „nicht mitgeschickt" (lass es) von „leer
 * mitgeschickt" (lösch es), und diesen Unterschied soll das Formular nicht
 * durch Fleiß zunichtemachen.
 */
function TripForm({ trip }: { trip: Trip }) {
  const texte = useTexte()
  const t = texte.reise.info
  const grenzen = useTextgrenzen()
  const [offen, setOffen] = useState(false)
  const [title, setTitle] = useState(trip.title)
  const [description, setDescription] = useState(trip.description)
  const [von, setVon] = useState(trip.starts_on ?? '')
  const [bis, setBis] = useState(trip.ends_on ?? '')
  const [zone, setZone] = useState(
    trip.tz_offset_min === null ? '' : String(trip.tz_offset_min),
  )
  const update = useUpdateTrip(trip.id)

  if (!offen) {
    return (
      <button
        type="button"
        className="button button--outline"
        onClick={() => setOffen(true)}
      >
        {t.angabenAendern}
      </button>
    )
  }

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        const geaendert: Parameters<typeof update.mutate>[0] = {}
        if (title !== trip.title) geaendert.title = title
        if (description !== trip.description) geaendert.description = description
        if (von !== (trip.starts_on ?? '')) geaendert.starts_on = von || null
        if (bis !== (trip.ends_on ?? '')) geaendert.ends_on = bis || null
        if (zone !== (trip.tz_offset_min === null ? '' : String(trip.tz_offset_min))) {
          geaendert.tz_offset_min = zone === '' ? '' : Number(zone)
        }

        if (Object.keys(geaendert).length === 0) {
          setOffen(false)
          return
        }
        update.mutate(geaendert, { onSuccess: () => setOffen(false) })
      }}
    >
      {update.isError && <p className="notice" role="alert">{update.error.message}</p>}

      <label className="field" style={{ marginBottom: 0 }}>
        <span className="field__label">{t.feldTitel}</span>
        <input
          className="field__input"
          required
          maxLength={grenzen.title}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <label className="field" style={{ marginBottom: 0 }}>
        <span className="field__label">{t.beschreibung}</span>
        <textarea
          className="field__input"
          rows={3}
          maxLength={grenzen.trip_description}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Restzeichen wert={description} grenze={grenzen.trip_description} />
      </label>

      <div className="row row--wrap">
        <label className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
          <span className="field__label">{t.von}</span>
          <input
            className="field__input"
            type="date"
            value={von}
            max={bis || undefined}
            onChange={(event) => setVon(event.target.value)}
          />
        </label>
        <label className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
          <span className="field__label">{t.bis}</span>
          <input
            className="field__input"
            type="date"
            value={bis}
            min={von || undefined}
            onChange={(event) => setBis(event.target.value)}
          />
        </label>
      </div>

      {/* **Die Uhr der Reise.** Ohne sie rechnet die Oberfläche jeden
          Zeitpunkt in die Uhr des Geräts um — wer den Bericht abends zu Hause
          durchsieht, läse jede Ankunft um Stunden verschoben. Sie gilt für
          alle Mitlesenden; einzelne Punkte lassen sich im Formular auf der
          Karte davon abweichend stellen. */}
      <label className="field" style={{ marginBottom: 0 }}>
        <span className="field__label">{t.zeitzone}</span>
        <select
          className="field__input"
          value={zone}
          onChange={(event) => setZone(event.target.value)}
        >
          <option value="">{t.zeitzoneOhne}</option>
          {versatzAuswahl().map((minuten) => (
            <option key={minuten} value={minuten}>
              {versatzText(minuten)}
            </option>
          ))}
        </select>
        <span className="field__hint">{t.zeitzoneHinweis}</span>
      </label>

      <div className="row">
        <button className="button" type="submit" disabled={update.isPending}>
          {update.isPending ? t.wirdGespeichert : texte.allgemein.speichern}
        </button>
        <button
          className="button button--outline"
          type="button"
          onClick={() => {
            setTitle(trip.title)
            setDescription(trip.description)
            setVon(trip.starts_on ?? '')
            setBis(trip.ends_on ?? '')
            setZone(trip.tz_offset_min === null ? '' : String(trip.tz_offset_min))
            setOffen(false)
          }}
        >
          {texte.allgemein.abbrechen}
        </button>
      </div>
    </form>
  )
}

/** Name und Adresse dessen, der anklopft. */
function Anfragender({ request }: { request: JoinRequest }) {
  return (
    <div className="row">
      <span className="avatar" style={{ width: 36, height: 36, fontSize: 14 }}>
        {initials(request.user.display_name)}
      </span>
      <span className="bar__grow">
        <span style={{ fontWeight: 600 }}>{request.user.display_name}</span>
        <br />
        {/* Die Adresse steht dabei, weil zwei Leute denselben Namen eintragen
            können und man beim Bestätigen nicht raten soll. */}
        <span style={{ fontSize: 13, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
          {request.email}
        </span>
      </span>
    </div>
  )
}

/**
 * Angaben zur Reise, Einladungen und Teilnehmer.
 *
 * Teilnehmerliste und Einladungen sieht nur der Absender -- Empfänger sollen
 * einander nicht kennen. Das Backend verweigert die Auskunft ohnehin; hier
 * wird sie gar nicht erst angefragt.
 */
export function TripInfoPage() {
  const texte = useTexte()
  const t = texte.reise.info
  const { tripId = '' } = useParams()
  const id = Number(tripId)
  const trip = useTrip(id)
  const isSender = trip.data?.role === 'sender'
  const aussteigen = useLeaveTrip(id)
  const navigate = useNavigate()

  const members = useMembers(id, isSender)
  const invitations = useInvitations(id, isSender)
  const createInvitation = useCreateInvitation(id)
  const revoke = useRevokeInvitation(id)
  const setTripState = useSetTripState(id)
  const removeMember = useRemoveMember(id)
  const setMuted = useSetMuted(id)
  const requests = useJoinRequests(id, isSender)
  const decide = useDecideRequest(id)
  const setApproval = useSetApproval(id)
  // Für die Marke „an alle" -- dieselbe Route wie Titel und Zeitraum.
  const updateTrip = useUpdateTrip(id)
  const setCutoff = useSetCutoff(id)
  const setAktiv = useSetAktiv(id)
  // Ob die zweite Ebene überhaupt angeboten wird: über ein Konto entscheidet
  // die Verwaltung, nicht der Absender einer Reise.
  const me = useMe()
  const [copied, setCopied] = useState<string | null>(null)
  // **Personen ohne Einladungslink zuordnen** (#36) -- das Blatt und der
  // Gruss, den die Zugeordneten bekommen.
  const [zuordnenOffen, setZuordnenOffen] = useState(false)

  /**
   * **Der Zeitraum des Buches — hier und nicht im Buch.**
   *
   * Er stand von Anfang an in der Adresse (`?von=&bis=`), aber es gab kein
   * Feld dafür: aus dem Betrieb gemeldet als „ich kann keinen Zeitraum
   * eingeben". Und er gehört hierher und nicht in die Buchseite: dort läge ein
   * Formular mitten in einem Dokument, das gerade gedruckt werden soll.
   *
   * **Leer heißt der ganze Bericht.** Beide Felder sind freiwillig und
   * unabhängig — „ab dem 24." ohne Ende ist eine sinnvolle Angabe.
   */
  const [buchVon, setBuchVon] = useState('')
  const [buchBis, setBuchBis] = useState('')
  const buchZeitraum = {
    von: buchVon === '' ? null : buchVon,
    bis: buchBis === '' ? null : buchBis,
  }
  const willkommen = useSendWelcome(id)

  /*
   * **Vom Sperrbildschirm direkt ans Freigabeformular (#46).**
   *
   * Aus dem Issue: „Kann die Chat-Nachricht direkt den Link enthalten für das
   * Benutzer/Freigabeformular?" Der Push zeigt auf diese Seite und trägt
   * `?anfragen=1` mit.
   *
   * **Hier stand ein Sprung, und jetzt steht eine Wahl.** Solange die Seite
   * eine lange Liste war, holte ein `scrollIntoView` die Karte „Möchte
   * mitlesen" ins Bild — mit einem Merker daneben, damit es nur einmal
   * geschieht, und einer Zeile, die den Parameter danach wieder entfernte.
   * Seit #39 gibt es Kategorien: der Verweis **wählt** die Kategorie, und die
   * ganze Mechanik entfällt ersatzlos. Nichts, was ins Leere springen kann.
   *
   * **Ein Suchparameter und kein Anker**, und das gilt weiter: `alsPfad()` in
   * `lib/tippziel.ts` gibt `pathname + search` zurück und wirft den Hash weg.
   * Ein `#anfragen` käme genau dann nie an, wenn die App schon offen ist.
   *
   * Umgeschrieben statt zweier Namen: `?anfragen=1` ist die Adresse, die
   * draußen im Umlauf ist — in jeder Benachrichtigung, die schon verschickt
   * wurde. Sie muss weiter gelten.
   */
  const [suche, setSuche] = useSearchParams()
  useEffect(() => {
    if (suche.get('anfragen') !== '1') return
    const naechste = new URLSearchParams(suche)
    naechste.delete('anfragen')
    naechste.set('bereich', 'anfragen')
    setSuche(naechste, { replace: true })
  }, [suche, setSuche])
  const zeitraum = tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)
  const wartende = requests.data?.filter((r) => r.state === 'pending') ?? []
  const abgelehnte = requests.data?.filter((r) => r.state === 'rejected') ?? []
  // Welche der drei Stufen gerade gilt. `hidden_at` wiegt schwerer, weil es
  // `archived_at` voraussetzt.
  const zustand: TripState =
    trip.data?.hidden_at != null ? 'private' : trip.data?.archived_at != null ? 'closed' : 'active'

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(url)
      setTimeout(() => setCopied(null), 2500)
    } catch {
      // Ohne Zwischenablage-Recht bleibt der Link sichtbar zum Markieren.
      setCopied(null)
    }
  }

  /*
   * **Die Einstellungen einer Reise als Kategorien (#39).**
   *
   * Das war die längste Scrollseite der Anwendung -- zwölf Karten
   * untereinander, vom Zeitraum über die Einladungslinks bis zum
   * Zurücksetzen. Sie benutzt dasselbe Bauteil wie die Einstellungen und die
   * Verwaltung: ab 900 Punkten Leiste und Inhalt nebeneinander, darunter
   * nacheinander.
   *
   * **Was jemand nicht darf, steht auch nicht als Kategorie da.** Eine leere
   * Schublade ist schlechter als keine -- ein Mitlesender bekommt weder
   * „Einladen" noch „Empfänger" noch „Zurücksetzen" angeboten. Entschieden
   * wird das hier aus `isSender` und der Inhaberschaft, geprüft ohnehin im
   * Server.
   */
  const kategorien: Kategorie[] = [
    {
      schluessel: 'reise',
      titel: t.kategorien.reise,
      symbol: <InfoIcon size={19} />,
      inhalt: () => (
        <>
          <div className="card card--seite">
            {zeitraum !== '' && (
              <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{zeitraum}</p>
            )}
            {trip.data?.description && <p>{trip.data.description}</p>}
            <p className="card__lead" style={{ marginBottom: isSender ? 12 : 0 }}>
              {isSender ? t.duBistAbsender : t.berichtVon(trip.data?.owner.display_name ?? '')}
            </p>

            {/* Auch eine abgeschlossene Reise lässt sich noch beschriften: die
                Beschreibung entsteht oft erst, wenn sie vorbei ist. */}
            {isSender && trip.data && (
              <TripForm key={trip.data.id} trip={trip.data} />
            )}
          </div>

          {/* **Das Buch** (#14) — nur für den Absender.

              Im Issue stand „nur für die Verwaltung"; seit #36 gehört jede
              Reise ihrem Reisenden, und die privaten Gespräche darin sind
              seine. Also der Absender **dieser** Reise. Der Server setzt das
              ohnehin durch: `partner()` lässt `?with=` nur bei ihm zu.

              Ein eigener Verweis und kein Knopf mit Sprung: das Buch ist eine
              Seite und keine Handlung, und ein Verweis lässt sich in einem
              zweiten Reiter öffnen. */}
          {isSender && (
            <div className="card card--seite">
              <h2 className="card__titel">{t.buch.titel}</h2>
              <p className="card__lead">
                {t.buch.leadVor}
                <strong>{t.buch.leadFett}</strong>
                {t.buch.leadNach}
              </p>
              {/* **Zwei Tage, beide freiwillig.** Dieselben Felder wie am
                  Reisezeitraum darüber -- gegenseitig begrenzt, damit „bis"
                  nicht vor „von" liegen kann und dabei stillschweigend ein
                  leeres Buch ergibt. */}
              <div className="row row--wrap" style={{ marginBottom: 12 }}>
                <label className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
                  <span className="field__label">{t.von}</span>
                  <input
                    className="field__input"
                    type="date"
                    value={buchVon}
                    max={buchBis || undefined}
                    onChange={(event) => setBuchVon(event.target.value)}
                  />
                </label>
                <label className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
                  <span className="field__label">{t.bis}</span>
                  <input
                    className="field__input"
                    type="date"
                    value={buchBis}
                    min={buchVon || undefined}
                    onChange={(event) => setBuchBis(event.target.value)}
                  />
                </label>
              </div>

              <p className="feld__hinweis feld__hinweis--13" style={{ marginTop: 0 }}>
                {t.buch.zeitraumHinweis}
              </p>

              <Link
                className="button button--outline"
                to={mitZeitraum(`/t/${id}/buch`, buchZeitraum)}
              >
                <BuchIcon size={16} />
                {t.buch.oeffnen}
              </Link>

              {/* **Je Person ein Dokument, und nie ein gemeinsames.** Ein
                  einziges „privat"-Dokument mit allen Gesprächen wäre das
                  Leck: Annas Antworten landen bei Bert, sobald die Datei
                  weitergereicht wird. */}
              {(members.data ?? []).filter((m) => m.role === 'recipient' && m.left !== true)
                .length > 0 && (
                <>
                  <p className="feld__hinweis feld__hinweis--13" style={{ marginTop: 14 }}>
                    {t.buch.jePerson}
                  </p>
                  <div className="row row--wrap" style={{ gap: 8 }}>
                    {(members.data ?? [])
                      .filter((m) => m.role === 'recipient' && m.left !== true)
                      .map((m) => (
                        <Link
                          key={m.user.id}
                          className="button button--outline button--small"
                          to={mitZeitraum(`/t/${id}/buch/privat/${m.user.id}`, buchZeitraum)}
                        >
                          {m.user.display_name}
                        </Link>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}
          {isSender && (
            <>
              {/* **Die Marke „aktiv" — genau eine, und nur an einer laufenden.**
                  Sie steht hier neben dem Zustand, weil sie eine Aussage
                  derselben Art ist: nicht, was jemand darf, sondern woran du
                  gerade bist. Angeboten wird sie nur an einer laufenden Reise —
                  der Server weist alles andere ab, und ein Knopf, der abgewiesen
                  wird, ist eine stille Absage mit Umweg.

                  Der Zustand darunter heißt „Läuft", diese Marke heißt „aktiv":
                  laufen können mehrere, unterwegs bist du auf einer. */}
              {zustand === 'active' && (
                <div className="card" style={{ margin: '16px auto 0', boxShadow: 'none' }}>
                  <h2 className="card__titel">{t.aktiv.titel}</h2>
                  <p className="card__lead">
                    {trip.data?.activated_at != null ? t.aktiv.gesetzt : t.aktiv.offen}
                  </p>

                  {setAktiv.isError && (
                    <p className="notice" role="alert">{setAktiv.error.message}</p>
                  )}

                  <button
                    className="button button--outline"
                    type="button"
                    disabled={setAktiv.isPending || trip.data === undefined}
                    onClick={() => setAktiv.mutate(trip.data?.activated_at == null)}
                  >
                    {trip.data?.activated_at != null ? t.aktiv.loesen : t.aktiv.setzen}
                  </button>
                </div>
              )}

              {/* **Trägt auch ein Beitrag an alle seine Marke?**

                  Aus dem Betrieb: „beide markieren, aber die Option auch im
                  Broadcast konfigurierbar machen für den Admin - auf
                  Reise-Ebene, nicht generell." Eine Aussage über *diesen*
                  Bericht, deshalb hier und nicht in den Einstellungen des
                  Kontos: wer zwei Reisen zugleich führt, eine stille und eine
                  mit vielen Einzelnachrichten, will es hier so und dort anders.

                  **Eine eigene Karte und nicht bei „Einladen".** Dort stand sie
                  zuerst, weil daneben schon eine Ankreuzfläche war -- mit dem
                  Einladen hat sie aber nichts zu tun. Aufgefallen ist es, weil
                  ein Prüflauf die *erste* Ankreuzfläche der Seite griff und
                  damit die falsche traf.

                  Die private Nachricht trägt ihre Marke unabhängig davon --
                  sie ist die Ausnahme, und die gehört benannt. */}
              <div className="card card--seite">
                <h2 className="card__titel">{t.kennzeichnung.titel}</h2>
                <label
                  className="row"
                  style={{ alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={trip.data?.mark_broadcast ?? true}
                    disabled={updateTrip.isPending}
                    onChange={(event) =>
                      updateTrip.mutate({ mark_broadcast: event.target.checked })
                    }
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <span style={{ fontWeight: 600 }}>{t.kennzeichnung.knopf}</span>
                    <br />
                    <span className="feld__hinweis feld__hinweis--13">
                      {(trip.data?.mark_broadcast ?? true)
                        ? t.kennzeichnung.an
                        : t.kennzeichnung.aus}
                    </span>
                  </span>
                </label>
              </div>

              {/* Drei Zustände, eine Leiter: jeder nimmt etwas mehr weg, und
                  keiner löscht etwas. Als Auswahl und nicht als Schalterkette,
                  weil man den Unterschied nur sieht, wenn alle drei
                  nebeneinander stehen. */}
              <div className="card" style={{ margin: '16px auto 0', boxShadow: 'none' }}>
                <h2 className="card__titel">{t.zustandTitel}</h2>

                {setTripState.isError && (
                  <p className="notice" role="alert">{setTripState.error.message}</p>
                )}

                <div className="stack" style={{ gap: 0 }}>
                  {STUFEN.map((stufe) => (
                    <label key={stufe.wert} className="choice">
                      <input
                        type="radio"
                        name="trip-state"
                        checked={zustand === stufe.wert}
                        disabled={setTripState.isPending}
                        onChange={async () => {
                          if (
                            stufe.frage === undefined ||
                            zustand === stufe.wert ||
                            (await rueckfrage(stufe.frage, stufe.titel))
                          ) {
                            setTripState.mutate(stufe.wert)
                          }
                        }}
                      />
                      <span>
                        <span style={{ fontWeight: 600 }}>{stufe.titel}</span>
                        <br />
                        <span className="feld__hinweis feld__hinweis--13">
                          {stufe.text}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* **Ganz unten und nur für den Inhaber** (#12). Was den Bericht
                  wegwirft, steht nicht zwischen den Einstellungen, sondern am
                  Ende — und `role === 'sender'` genügt dafür nicht: eine Reise
                  kann mehrere Absender haben, gehören tut sie einem. Der Server
                  prüft dasselbe noch einmal. */}
            </>
          )}
        </>
      ),
    },
    // **Die Kategorie steht bei jedem** (#52). Der Reisende bestimmt das
    // Aussehen des Berichts; ein Mitlesender stellt hier genau eine Sache ein
    // -- die Farbe seiner Sprechblasen. Der Hintergrund gehört dem Bericht und
    // steht deshalb nur beim Reisenden.
    ...(trip.data
      ? [{
          schluessel: 'aussehen',
          titel: t.kategorien.aussehen,
          symbol: <PhotoIcon size={19} />,
          inhalt: () => (
            <>
              {!isSender && trip.data && <MeineBlasenfarben trip={trip.data} />}

              {/* Direkt unter den Angaben: es gehört zum Bild der Reise nach außen,
                  wie Titel und Zeitraum. Und es steht nur beim Reisenden. */}
              {isSender && trip.data && <Symbolbild trip={trip.data} />}

              {isSender && trip.data && <Chatgrund trip={trip.data} />}

              {isSender && trip.data && <Blasenfarben trip={trip.data} />}
            </>
          ),
        }]
      : []),
    {
      schluessel: 'benachrichtigung',
      titel: t.kategorien.benachrichtigung,
      symbol: <GlockeIcon size={19} />,
      inhalt: () => (
        <>
          <div className="card card--seite">
            <h2 className="card__titel">{t.stumm.titel}</h2>
            {/* **Die Wirkung, die man eigentlich sucht, stand hier nicht.** Der
                Schalter regelt nicht nur die Hervorhebung in der Liste, sondern
                auch, ob für diese Reise Push-Nachrichten aufs Gerät kommen — und
                genau deshalb sucht man ihn. Aus dem Betrieb gemeldet. */}
            <p className="card__lead">
              {trip.data?.muted ? t.stumm.istStumm : t.stumm.laut}
            </p>
            <button
              className="button button--outline"
              type="button"
              disabled={setMuted.isPending || trip.data === undefined}
              onClick={() => setMuted.mutate(!trip.data?.muted)}
            >
              {trip.data?.muted ? t.stumm.aufheben : t.stumm.setzen}
            </button>
          </div>
        </>
      ),
    },
    ...(isSender
      ? [
          {
            schluessel: 'einladen',
            titel: t.kategorien.einladen,
            symbol: <LinkIcon size={19} />,
            inhalt: () => (
              <>
                <div className="card card--seite">
                  <h2 className="card__titel">{t.einladen.titel}</h2>
                  <p className="card__lead">
                    {trip.data?.approval_required
                      ? t.einladen.mitBestaetigung
                      : t.einladen.ohneBestaetigung}
                  </p>

                  {invitations.data?.map((invitation) => (
                    <div key={invitation.code} className="stack" style={{ marginBottom: 14 }}>
                      <div className="row">
                        <span className="code">{invitation.code}</span>
                        {/* **Was hier stand, war wahr und trotzdem irreführend.**
                            `uses` steigt erst mit deiner Freigabe -- also stand
                            „noch nicht benutzt" da, während vier Menschen auf eine
                            Antwort warteten. Gemeldet aus dem Betrieb, mit
                            Bildschirmfoto. Wer wartet, gehört zuerst genannt: das
                            ist das, was eine Handlung verlangt. */}
                        <span className="feld__hinweis feld__hinweis--13">
                          {invitation.wartend > 0
                            ? invitation.wartend === 1
                              ? t.einladen.einerWartet
                              : t.einladen.wartenAufDich(invitation.wartend)
                            : invitation.uses === 0
                              ? t.einladen.nochNichtBenutzt
                              : invitation.uses === 1
                                ? t.einladen.einmalBenutzt
                                : t.einladen.malBenutzt(invitation.uses)}
                        </span>
                      </div>
                      {/* Was der Link kann, und was er nicht ist: keine Obergrenze
                          für die Reise, sondern für diesen einen Link. Die alte
                          Zeile „1 von 25 eingelöst" las sich wie eine Grenze für
                          die Zahl der Mitlesenden. */}
                      <span className="feld__hinweis feld__hinweis--13">
                        {invitation.max_uses === 1
                          ? t.einladen.fuerEine
                          : t.einladen.fuerMehrere(invitation.max_uses)}
                        {t.einladen.laeuftAm}
                        {dayMonth(invitation.expires_at)}
                        {t.einladen.ab}
                      </span>
                      <div className="row">
                        <button
                          className="button button--outline"
                          type="button"
                          onClick={() => void copy(invitation.url)}
                        >
                          {copied === invitation.url ? t.einladen.kopiert : t.einladen.kopieren}
                        </button>
                        {/* Ein Link, der aus der Hand ist, lässt sich nicht
                            zurückholen -- ihn ungültig zu machen ist die einzige
                            Antwort darauf. Mit Rückfrage, denn rückgängig macht
                            das niemand: wer den Code schon hat, steht danach vor
                            einer Tür, die nicht mehr aufgeht. */}
                        <button
                          className="button button--outline button--small"
                          type="button"
                          disabled={revoke.isPending}
                          onClick={async () => {
                            if (
                              await rueckfrage(
                                t.einladen.deaktivierenFrage,
                                t.einladen.deaktivierenKurz,
                              )
                            ) {
                              revoke.mutate(invitation.code)
                            }
                          }}
                        >
                          {t.einladen.deaktivieren}
                        </button>
                      </div>
                      {revoke.isError && <p className="notice" role="alert">{revoke.error.message}</p>}
                      <code style={{ fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                        {invitation.url}
                      </code>
                    </div>
                  ))}

                  {/* Ein Link in eine zurückgezogene Reise führt ins Leere. Das
                      gehört hierher und nicht erst in die Fehlermeldung dessen,
                      der ihn öffnet. */}
                  {zustand === 'private' && (
                    <p className="notice notice--info">
                      {t.einladen.keinLinkHinein}
                    </p>
                  )}

                  <div className="row">
                    <button
                      className="button"
                      type="button"
                      disabled={createInvitation.isPending || zustand === 'private'}
                      onClick={() => createInvitation.mutate(undefined)}
                    >
                      {createInvitation.isPending
                        ? texte.allgemein.laeuft
                        : t.einladen.neuerLink}
                    </button>
                    {/* Ein Link, der nach dem ersten Beitritt verbraucht ist:
                        weitergereicht nützt er dann niemandem mehr. */}
                    <button
                      className="button button--outline"
                      type="button"
                      disabled={createInvitation.isPending || zustand === 'private'}
                      onClick={() => createInvitation.mutate(1)}
                    >
                      {t.einladen.linkFuerEine}
                    </button>
                  </div>

                  {/* Direkt beim Link, weil er die Frage aufwirft: ein Link wird
                      weitergereicht, und dieser Schalter entscheidet, ob das
                      genügt. */}
                  <label
                    className="row"
                    style={{ marginTop: 16, alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={trip.data?.approval_required ?? true}
                      disabled={setApproval.isPending}
                      onChange={(event) => setApproval.mutate(event.target.checked)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ fontWeight: 600 }}>{t.einladen.bestaetigenKnopf}</span>
                      <br />
                      <span className="feld__hinweis feld__hinweis--13">
                        {trip.data?.approval_required
                          ? t.einladen.bestaetigenAn
                          : t.einladen.bestaetigenAus}
                      </span>
                    </span>
                  </label>
                </div>
              </>
            ),
          },
          {
            schluessel: 'anfragen',
            titel: t.kategorien.anfragen,
            symbol: <PersonPlusIcon size={19} />,
            /*
             * **Die Zahl steht an der Zeile, nicht erst in der Kategorie.**
             * Aus dem Betrieb: „bei Anfragen: Zähler in der Leiste anzeigen,
             * wieviele Anfragen offen sind (in rot)." Wer die Kategorie nicht
             * öffnet, sieht sonst nicht, dass jemand wartet -- und eine
             * Anfrage, die man übersieht, ist für den Anfragenden nicht von
             * einer Ablehnung zu unterscheiden.
             *
             * Gezählt werden die **wartenden**, nicht alle: eine abgelehnte
             * ist entschieden und steht nur noch da, damit man sie
             * zurücknehmen kann.
             */
            zaehler: wartende.length,
            inhalt: () => (
              <>
                {/* Ganz oben, sobald jemand wartet: eine Anfrage, die man
                    übersieht, ist für den Anfragenden nicht von einer
                    Ablehnung zu unterscheiden.

                    Die abgelehnten stehen mit dabei, still und weiter unten. Eine
                    Entscheidung, die man nicht mehr sieht, lässt sich nicht
                    revidieren -- und man weiß später nicht einmal mehr, dass man
                    sie getroffen hat. */}
                {/*
                    **Eine Kategorie, die leer sein kann, sagt es.**

                    Sie steht immer da und verschwindet nicht, sobald der
                    letzte Antrag entschieden ist -- eine Schublade, die
                    auftaucht und wieder weggeht, ist unruhig. Und wer über
                    die Benachrichtigung hierher kommt, nachdem er den Antrag
                    schon von einem anderen Gerät aus angenommen hat, soll
                    nicht vor nichts stehen.
                */}
                {(requests.data?.length ?? 0) === 0 && (
                  <div className="card card--seite">
                    <h2 className="card__titel">{t.anfragen.titel}</h2>
                    <p className="card__lead" style={{ marginBottom: 0 }}>
                      {t.anfragen.niemand}
                    </p>
                  </div>
                )}
                {(requests.data?.length ?? 0) > 0 && (
                  <div className="card card--seite">
                    {/* Ohne Wartende trägt die Karte den zweiten Titel -- „Möchte
                        mitlesen (0)" wäre eine Überschrift über nichts. */}
                    <h2 className="card__titel">
                      {wartende.length > 0
                        ? t.anfragen.mitZahl(wartende.length)
                        : t.anfragen.abgelehnteTitel}
                    </h2>
                    {wartende.length > 0 && (
                      <p className="card__lead">{t.anfragen.lead}</p>
                    )}

                    {decide.isError && <p className="notice" role="alert">{decide.error.message}</p>}

                    <div className="stack">
                      {wartende.map((request) => (
                        <div
                          key={request.user.id}
                          className="stack"
                          style={{ gap: 8, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}
                        >
                          <Anfragender request={request} />

                          <div className="row">
                            <button
                              type="button"
                              className="button button--small"
                              disabled={decide.isPending}
                              onClick={async () => {
                                /*
                                  **Die Grenze des Links fragt, sie sperrt nicht.**

                                  Sie soll verhindern, dass ein weitergereichter
                                  Link ohne deine Entscheidung viele hereinlässt.
                                  Stehst du selbst davor, ist die Entscheidung da --
                                  dir dann nein zu sagen wäre ein kaputter Knopf,
                                  und der Anwärter bliebe für immer hängen.

                                  Der Server antwortet beim ersten Versuch mit 409;
                                  erst danach wird gefragt. Die Rückfrage vorweg zu
                                  stellen hieße, die Regel ein zweites Mal im
                                  Browser zu führen.
                                */
                                try {
                                  await decide.mutateAsync({
                                    userId: request.user.id,
                                    approve: true,
                                  })
                                } catch (fehler) {
                                  if (!(fehler instanceof ApiError) || fehler.status !== 409) return
                                  if (
                                    await rueckfrage(
                                      t.anfragen.trotzdemFrage(
                                        fehler.message,
                                        request.user.display_name,
                                      ),
                                      t.anfragen.trotzdem,
                                    )
                                  ) {
                                    decide.mutate({
                                      userId: request.user.id,
                                      approve: true,
                                      trotzdem: true,
                                    })
                                  }
                                }
                              }}
                            >
                              {t.anfragen.aufnehmen}
                            </button>
                            <button
                              type="button"
                              className="button button--outline button--small"
                              disabled={decide.isPending}
                              onClick={async () => {
                                if (
                                  await rueckfrage(
                                    t.anfragen.ablehnenFrage(request.user.display_name),
                                    t.anfragen.ablehnen,
                                  )
                                ) {
                                  decide.mutate({ userId: request.user.id, approve: false })
                                }
                              }}
                            >
                              {t.anfragen.ablehnenKnopf}
                            </button>
                          </div>

                          {/* Die zweite Ebene, und nur für die Verwaltung: wer
                              über einen weitergereichten Link hereinkommt, soll
                              nicht bei der nächsten Reise wieder vor der Tür
                              stehen. Das Konto geht über diese Reise hinaus --
                              deshalb entscheidet darüber nicht der Absender. */}
                          {me.data?.may_manage_people && (
                            <button
                              type="button"
                              className="button button--outline button--small button--gefahr"
                              style={{ alignSelf: 'flex-start' }}
                              disabled={decide.isPending}
                              onClick={async () => {
                                if (
                                  await rueckfrage(
                                    t.anfragen.sperrenFrage(request.user.display_name),
                                    t.anfragen.sperren,
                                  )
                                ) {
                                  decide.mutate({
                                    userId: request.user.id,
                                    approve: false,
                                    disableAccount: true,
                                  })
                                }
                              }}
                            >
                              {t.anfragen.sperrenKnopf}
                            </button>
                          )}
                        </div>
                      ))}

                      {abgelehnte.length > 0 && (
                        <div className="stack" style={{ gap: 8 }}>
                          {wartende.length > 0 && (
                            <h2 style={{ fontSize: 14, margin: '4px 0 0', color: 'var(--text-muted)' }}>
                              {t.anfragen.abgelehntMitZahl(abgelehnte.length)}
                            </h2>
                          )}
                          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                            {t.anfragen.abgelehntLead}
                          </p>

                          {abgelehnte.map((request) => (
                            <div
                              key={request.user.id}
                              className="stack"
                              style={{ gap: 6, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}
                            >
                              <Anfragender request={request} />
                              <button
                                type="button"
                                className="button button--outline button--small"
                                style={{ alignSelf: 'flex-start' }}
                                disabled={decide.isPending}
                                onClick={() =>
                                  decide.mutate({ userId: request.user.id, approve: true })
                                }
                              >
                                {t.anfragen.dochAufnehmen}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ),
          },
          {
            schluessel: 'empfaenger',
            titel: t.kategorien.empfaenger,
            symbol: <GruppeIcon size={19} />,
            inhalt: () => (
              <>
                <div className="card card--seite" style={{ marginBottom: 0 }}>
                  <h2 className="card__titel">
                    {t.empfaenger.titelMitZahl(
                      members.data?.filter((m) => m.role === 'recipient' && !m.left).length ?? 0,
                    )}
                  </h2>
                  <p className="card__lead">{t.empfaenger.lead}</p>

                  {/* **Der zweite Weg herein** (#36). Wer schon ein Konto
                      hat, braucht keinen Einladungslink -- ein Häkchen im
                      Blatt genügt, und es wirkt sofort. */}
                  <button
                    type="button"
                    className="button button--outline button--small"
                    style={{ alignSelf: 'flex-start', marginBottom: 12 }}
                    onClick={() => setZuordnenOffen(true)}
                  >
                    <PersonPlusIcon size={15} />
                    {t.empfaenger.zuordnen}
                  </button>

                  {/* **Eine Legende, einmal oben** (#32).

                      Symbole in jeder Zeile brauchen eine Stelle, an der steht,
                      was sie bedeuten. Ein `title` genügt dafür nicht: auf dem
                      Telefon ist er unsichtbar — dieselbe Regel wie bei den
                      Fehlermeldungen. Also einmal hier in Worten, und in den
                      Zeilen nur noch das Bild. */}
                  <p className="feld__hinweis feld__hinweis--13 zeichenerklaerung">
                    <span>
                      <GlockeIcon size={15} />
                      {t.empfaenger.legendeErreichbar}
                    </span>
                    <span>
                      <GlockeAusIcon size={15} />
                      {t.empfaenger.legendeStumm}
                    </span>
                    <span>
                      <MondIcon size={15} />
                      {t.empfaenger.legendeStummOhne}
                    </span>
                  </p>

                  {removeMember.isError && (
                    <p className="notice" role="alert">{removeMember.error.message}</p>
                  )}
                  {willkommen.isError && (
                    <p className="notice" role="alert">{willkommen.error.message}</p>
                  )}

                  <div className="stack">
                    {members.data
                      ?.filter((member) => member.role === 'recipient')
                      .map((member) => (
                        <div
                          key={member.user.id}
                          className="stack"
                          style={{ gap: 6, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}
                        >
                          <div className="row">
                            <Link
                              className="row bar__grow"
                              to={`/t/${id}/with/${member.user.id}`}
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              <span className="avatar" style={{ width: 36, height: 36, fontSize: 14 }}>
                                {initials(member.user.display_name)}
                              </span>
                              <span style={member.disabled ? { fontStyle: 'italic' } : undefined}>
                                {member.user.display_name}
                                {/* Selbst gegangen. Zuerst, weil es alles andere
                                    erklärt: an dieser Zeile hängt nichts mehr. */}
                                {member.left && (
                                  <span className="feld__hinweis feld__hinweis--13">
                                    {t.empfaenger.ausgestiegen}
                                  </span>
                                )}
                                {/* Stillgelegt zuerst: solange das Konto zu ist,
                                    ist alles andere nachrangig. */}
                                {member.disabled && !member.left && (
                                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                                    {t.empfaenger.stillgelegt}
                                  </span>
                                )}
                                {member.cut_off && (
                                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                                    {t.empfaenger.nichtsNeues}
                                  </span>
                                )}
                              </span>
                              {/* **Erreichbarkeit und Ruhe, als ein Zeichen** (#32).

                                  Wer gegangen oder stillgelegt ist, bekommt keins:
                                  dort sagt das Wort daneben schon alles, und ein
                                  zweites Zeichen wäre eine Aussage über jemanden,
                                  der nicht mehr dabei ist. Und wer erreichbar ist
                                  und nichts stummgeschaltet hat, bekommt auch
                                  keins — der Normalfall braucht kein Abzeichen. */}
                              {!member.left && !member.disabled && (
                                <Erreichbarkeit
                                  push={member.push === true}
                                  stumm={member.muted === true}
                                />
                              )}
                            </Link>

                            {/* Wer gegangen ist, lässt sich weder entfernen noch
                                abschneiden -- er ist kein Mitglied mehr. Die Zeile
                                steht nur noch da, damit niemand spurlos fehlt. */}
                            {!member.left && <button
                              type="button"
                              className="icon-button"
                              title={t.empfaenger.entfernen(member.user.display_name)}
                              aria-label={t.empfaenger.entfernen(member.user.display_name)}
                              disabled={removeMember.isPending}
                              onClick={async () => {
                                // Was sie geschrieben hat, bleibt dir erhalten --
                                // das gehört in die Frage hinein, sonst zögert man
                                // aus dem falschen Grund.
                                if (
                                  await rueckfrage(
                                    t.empfaenger.entfernenFrage(member.user.display_name),
                                    t.empfaenger.entfernenKnopf,
                                  )
                                ) {
                                  removeMember.mutate(member.user.id)
                                }
                              }}
                            >
                              <XIcon size={16} />
                            </button>}
                          </div>

                          {/* **Der Gruss an genau diesen einen** (#36).

                              Er steht nur da, wo er etwas tut: solange ein
                              Text hinterlegt ist, die Person noch keinen hat
                              und noch dabei ist. Ein Knopf, der grau
                              danebensteht, ist eine Frage ohne Antwort. */}
                          {!member.left &&
                            !member.disabled &&
                            member.welcomed !== true &&
                            (trip.data?.welcome_text ?? '') !== '' && (
                              <button
                                type="button"
                                className="button button--outline button--small"
                                style={{ alignSelf: 'flex-start' }}
                                disabled={willkommen.isPending}
                                onClick={() => willkommen.mutate(member.user.id)}
                              >
                                <SendIcon size={15} />
                                {t.empfaenger.willkommenSenden}
                              </button>
                            )}

                          {/* Der leise Weg neben dem sichtbaren -- und mit Worten,
                              nicht nur mit einem Zeichen. Ein Pausesymbol neben
                              einem Namen sagt niemandem, was es bewirkt, und ein
                              Knopf, den man nicht findet, ist keiner. */}
                          {!member.left && <button
                            type="button"
                            className="button button--outline button--small"
                            style={{ alignSelf: 'flex-start' }}
                            disabled={setCutoff.isPending}
                            onClick={async () => {
                              if (
                                member.cut_off ||
                                (await rueckfrage(
                                  t.empfaenger.abschneidenFrage(member.user.display_name),
                                  t.empfaenger.abschneiden,
                                ))
                              ) {
                                setCutoff.mutate({ userId: member.user.id, cut: !member.cut_off })
                              }
                            }}
                          >
                            {member.cut_off ? <PlayIcon size={15} /> : <PauseIcon size={15} />}
                            {member.cut_off
                              ? t.empfaenger.wiederBeliefern
                              : t.empfaenger.abschneidenKnopf}
                          </button>}
                        </div>
                      ))}

                    {members.data?.every((member) => member.role !== 'recipient') && (
                      <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                        {t.empfaenger.niemand}
                      </p>
                    )}
                  </div>
                </div>
              </>
            ),
          },
          {
            schluessel: 'willkommen',
            // **Der Schlüssel bleibt `willkommen`**, auch wenn die
            // Beschriftung länger wurde: er steht als `?bereich=willkommen`
            // in der Adresse. Ihn mitzuändern wäre ein gebrochener Verweis
            // für einen Wortwunsch.
            titel: t.kategorien.willkommen,
            symbol: <SendIcon size={19} />,
            inhalt: () => <Willkommenskarte tripId={id} />,
          },
          {
            schluessel: 'zuruecksetzen',
            titel: t.kategorien.zuruecksetzen,
            symbol: <ResetIcon size={19} />,
            inhalt: () => (
              <>
                {trip.data !== undefined && trip.data.owner.id === me.data?.id && (
                  <Zuruecksetzen tripId={id} titel={trip.data.title} />
                )}
              </>
            ),
          },
        ]
      : [
          {
            schluessel: 'verlassen',
            titel: t.kategorien.verlassen,
            symbol: <ExitIcon size={19} />,
            inhalt: () => (
              <>
                {/* **Raus heißt raus** -- und nur für Mitlesende. Dem Absender gehört
                    die Reise; für ihn gibt es „abschließen" und „zurückziehen"
                    weiter unten. */}
                {!isSender && (
                  <div className="card card--seite">
                    <h2 className="card__titel">{t.verlassen.titel}</h2>
                    <p className="card__lead">
                      {t.verlassen.leadVor}
                      <strong>{t.verlassen.leadFett}</strong>
                      {t.verlassen.leadNach}
                    </p>
                    <button
                      className="button button--outline"
                      type="button"
                      disabled={aussteigen.isPending}
                      onClick={async () => {
                        if (
                          await rueckfrage(
                            t.verlassen.frage(trip.data?.title ?? t.verlassen.dieseReise),
                            t.verlassen.aussteigen,
                          )
                        ) {
                          // **Erst weg, dann aufräumen.** Nach dem Ausstieg gibt es
                          // diese Reise für dieses Konto nicht mehr; bliebe man auf
                          // ihrer Seite stehen, liefe sie in ein 404. `replace`,
                          // damit der Zurück-Knopf nicht dorthin zurückführt.
                          await aussteigen.mutateAsync()
                          navigate('/', { replace: true })
                        }
                      }}
                    >
                      {aussteigen.isPending ? texte.allgemein.laeuft : t.verlassen.aussteigen}
                    </button>
                    {aussteigen.isError && (
                      <p className="notice" role="alert">
                        {aussteigen.error instanceof Error
                          ? aussteigen.error.message
                          : texte.allgemein.schiefgelaufen}
                      </p>
                    )}
                  </div>
                )}
              </>
            ),
          },
        ]),
  ]

  return (
    <>
      <Netzstreifen />
      <Kategorien
        titel={trip.data?.title ?? t.kategorien.reise}
        zurueck={
          <Link className="icon-button" to={`/t/${id}`} aria-label={t.zurueck}>
            <BackIcon />
          </Link>
        }
        kategorien={kategorien}
      />
      {/* **Das Blatt liegt über der Seite, nicht in der Kategorie** (#36).
          In den Inhalt einer Kategorie gesetzt läge es im Fluss der Karte und
          wäre auf 390 Punkten von deren Rand beschnitten. */}
      {zuordnenOffen && (
        <Zuordnungsblatt tripId={id} onZu={() => setZuordnenOffen(false)} />
      )}
    </>
  )
}
